import {
  defineStore,
  generateInitialState,
  resetState,
  type SetupStore,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";
import { getCase } from "#src/services/http-cases.service.ts";
import { STEP } from "#src/constants/step.ts";

import { useFlowStore } from "#src/stores/flow.ts";
import { type StoreGeneric, type Pinia } from "pinia";
import { type Mode } from "#src/constants/mode.ts";
import { type RoleType } from "#src/types/Role.ts";

interface Product {
  name: string | null;
}

interface Carrier {
  avatar_url: string | null;
  name: string | null;
}

interface CasePrimaryAgent {
  name: string | null;
  email: string | null;
  avatar_url: string | null;
  type: string | null;
  scheduling_link: string | null;
  phone_work: string | null;
}

interface TimelineItem {
  key: string;
  inforce_date: string;
}
interface RawCaseRequirement {
  id: number;
  type: string;
  status: string;
  description: string;
  email: number;
  order: {
    id: number;
  };
  completed: boolean;
  dates: {
    completed_at?: string | null;
    updated_at?: string | null;
    created_at?: string | null;
    follow_up_date?: string | null;
  };
}

export interface CaseContractPartyRole {
  role: RoleType;
  beneficiary_amount?: number;
}

export interface CaseContractParty {
  name: string;
  roles: CaseContractPartyRole[];
}

function initializeProduct(model?: Partial<Product>) {
  return {
    name: model?.name || null,
  };
}

function initializeCarrier(model?: Partial<Carrier>) {
  return {
    avatar_url: model?.avatar_url || null,
    name: model?.name || null,
  };
}

function initializePrimaryAgent(
  model?: Partial<CasePrimaryAgent>
): CasePrimaryAgent {
  return {
    name: model?.name || null,
    phone_work: model?.phone_work || null,
    email: model?.email || null,
    type: model?.type || null,
    avatar_url: model?.avatar_url || null,
    scheduling_link: model?.scheduling_link || null,
  };
}

interface Case extends SetupStore, ToSetupState<CaseState> {
  setIds(args: { id: number; uuid: string }): void;
  loginToCase(birthdate: string): Promise<void>;
}

interface CaseState {
  id: number | null;
  uuid: string | null;
  eapp_id: number | null;
  status: string | null;
  premium: number | null;
  mode: Mode | null;
  policy_number: string | null;
  state: string | null;
  applied_rating: string | null;
  approved_rating: string | null;
  applied_premium: number | null;
  approved_premium: number | null;
  line: string | null;
  face_amount: number | null;
  pool_of_money: number | null;
  product: Product;
  carrier: Carrier;
  primary_agent: CasePrimaryAgent;
  inforce_date: string | null;
  sharable_apply_link: string | null;
  requirements: string[];
  contract_parties: CaseContractParty[];
}

function initialState(): CaseState {
  return {
    id: null,
    uuid: null,
    eapp_id: null,
    status: null,
    premium: null,
    mode: null,
    policy_number: null,
    state: null,
    applied_rating: null,
    approved_rating: null,
    applied_premium: null,
    approved_premium: null,
    line: null,
    face_amount: null,
    pool_of_money: null,
    product: initializeProduct(),
    carrier: initializeCarrier(),
    primary_agent: initializePrimaryAgent(),
    inforce_date: null,
    sharable_apply_link: null,
    requirements: [],
    contract_parties: [],
  };
}

function initializeCase(pinia: Pinia): Case {
  const state = generateInitialState<CaseState>(initialState());

  function $reset() {
    resetState<CaseState>(state, initialState());
  }

  function setIds(data: { id: number; uuid: string }) {
    state.id.value = data.id;
    state.uuid.value = data.uuid;
  }

  async function loginToCase(birthdate: string) {
    if (!state.id.value || !state.uuid.value) return;
    const caseData = await getCase(pinia, {
      id: state.id.value,
      uuid: state.uuid.value,
      birthdate,
    });

    state.status.value = caseData.status;
    state.premium.value = caseData.premium;
    state.mode.value = caseData.mode;
    state.policy_number.value = caseData.policy_number;
    state.state.value = caseData.state;
    state.applied_rating.value = caseData.applied_rating;
    state.approved_rating.value = caseData.approved_rating;
    state.applied_premium.value = caseData.applied_premium;
    state.approved_premium.value = caseData.approved_premium;
    state.line.value = caseData.line;
    state.sharable_apply_link.value = caseData.sharable_apply_link;
    state.eapp_id.value = caseData.eapp?.id;

    state.face_amount.value = caseData.face_amount;
    state.pool_of_money.value = caseData.pool_of_money;

    if (caseData.product) {
      state.product.value = initializeProduct(caseData.product);
    }

    if (caseData.carrier) {
      state.carrier.value = initializeCarrier(caseData.carrier);
    }

    if (caseData.primary_agent) {
      state.primary_agent.value = initializePrimaryAgent(
        caseData.primary_agent
      );
      if (state.primary_agent.value.scheduling_link) {
        const link = state.primary_agent.value.scheduling_link
          .replace("https://", "")
          .replace("http://", "");
        state.primary_agent.value.scheduling_link = `https://${link}`;
      }
    }

    if (Array.isArray(caseData.timeline)) {
      const inforceDate = caseData.timeline.find(
        (v: TimelineItem) => v?.key === "inforce_date"
      );
      state.inforce_date.value = inforceDate?.date || null;
    }

    if (Array.isArray(caseData.requirements)) {
      state.requirements.value = caseData.requirements
        .map((r: RawCaseRequirement) => r?.description)
        .filter(Boolean);
    }

    if (Array.isArray(caseData.contract_parties)) {
      state.contract_parties.value = caseData.contract_parties.map(
        (party: CaseContractParty) => {
          return {
            name: party.name,
            roles: party.roles.map(r => ({
              role: r.role,
              beneficiary_amount: r.beneficiary_amount,
            })),
          };
        }
      );
    }
  }

  return {
    $reset,
    ...state,
    setIds,
    loginToCase,
  };
}

export const useCaseStore = (pinia: Pinia, hot?: StoreGeneric) => {
  return defineStore<string, Case>("case", () => {
    return initializeCase(pinia);
  })(pinia, hot);
};
